import { Container } from '@mui/material'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import React from 'react'
import { addApolloState, initializeApollo } from '../../lib/apolloClient'
import NotFound from '../../src/components/Modules/NotFound'
import StylePage from '../../src/components/Modules/StylePage'
import Layout from '../../src/constants/Layout'
import { AppContextWrapper } from '../../src/context/appContext'
import { CategoryContextWrapper } from '../../src/context/pagenumberContext'
import {
  GET_MIN_MAX_DIMENSIONS_STATIC,
  GET_MIN_MAX_PRICE_STATIC,
  GET_PARENT_CATEGORIES,
  GET_PARENT_CATEGORIES_NON_RECURSIVE,
  GET_SORTED_BY_POSITION_PRODUCTS_STYLE,
  GET_STYLE_DATA,
} from '../../src/operations/queries'
import { GET_STYLE_PERMALINKS } from '../../src/operations/queries/sitemapQueries'
import { APP_SHOP_SETTINGS } from '../../src/operations/queries/_appQueries'
import { sortCategories } from '../../src/utilityFunctions/categoryUtils'
import { getProfileIndexFromLanguage } from '../../src/utilityFunctions/getProfile'
import { styleMetaTags } from '../../src/utilityFunctions/metaUtils/styleMetaTags'
import { useShopSettingsInit } from '../../src/utilityFunctions/useShopSettingsInit'

const Style = ({
  currentStyleData,
  staticData,
  styleName,
  styleDescription,
  categoriesData,
  permalink,
  productsMinMaxPrice,
  productsMinMaxDimensions,
  shopSettingsData,
  pageNumber,
  locale,
  locales,
  defaultLocale,
  _nextI18Next,
  ...props
}) => {
  const { t } = useTranslation(['common'])
  const categorySlug = t('category-slug')
  const styleSlug = t('style-slug')
  const productSlug = t('product-slug')
  const { initialI18nStore } = _nextI18Next
  useShopSettingsInit(shopSettingsData.data.findFirstShopSetting)
  const shopSettings = { ...shopSettingsData.data.findFirstShopSetting }

  const productContainers =
    staticData.data.sortedByStylePositionProductContainers
      .sortedProductContainers

  const productsCount =
    staticData.data.sortedByStylePositionProductContainers.productsCount

  // ===== USER AGENT ==== information about incoming user
  // We use this info to determine if its Safari (as it doesnt support webp images)
  let ua
  // if (props.uaString) {
  //   ua = parse(props.uaString)
  // } else {
  //   ua = parse(window.navigator.userAgent)
  // }

  // ========= Return 404 if no data is found...
  if (!currentStyleData?.data?.findUniqueMetaInformation) {
    return (
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <Layout categoriesData={categoriesData} locale={locale}>
          <Container maxWidth="xl">
            <NotFound />
          </Container>
        </Layout>
      </AppContextWrapper>
    )
  }

  return (
    <>
      {styleMetaTags({
        currentStyleData,
        locale,
        locales,
        shopSettingsData,
        productsCount,
        pageNumber,
        styleSlug,
        defaultLocale,
        initialI18nStore,
        productSlug,
        productContainers,
      })}
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <CategoryContextWrapper data={{ staticData }} locale={locale}>
          <Layout categoriesData={categoriesData} locale={locale}>
            <StylePage
              staticData={
                staticData.data.sortedByStylePositionProductContainers
                  .sortedProductContainers
              }
              styleName={styleName}
              styleDescription={styleDescription}
              permalink={permalink}
              productsCount={
                staticData.data.sortedByStylePositionProductContainers
                  .productsCount
              }
              productsMinMaxPriceStatic={productsMinMaxPrice}
              numberOfPages={
                staticData.data.sortedByStylePositionProductContainers
                  .numberOfPages
              }
              productsMinMaxDimensions={productsMinMaxDimensions}
              pageNumber={pageNumber}
            />
          </Layout>
        </CategoryContextWrapper>
      </AppContextWrapper>
    </>
  )
}

const language = process.env.DEFAULT_LOCALE
const itemsPerPage = process.env.NEXT_PUBLIC_ITEMS_PER_CATEGORY_PAGE
  ? parseInt(process.env.NEXT_PUBLIC_ITEMS_PER_CATEGORY_PAGE)
  : 32

//* ==================================================================================================
//* ======================================= GET STATIC PATHS =========================================
//* ==================================================================================================
export const getStaticPaths: GetStaticPaths = async (ctx) => {
  const apolloClient = initializeApollo()
  // ==============================    TESTING SPACE ===========================

  // ================== STYLE =============================mano
  // --- GET STYLE PERMALINKS  FROM BACKEND
  const stylePermalinksQuery = await apolloClient.query({
    query: GET_STYLE_PERMALINKS,
    variables: {
      language: language,
    },
  })

  let stylePermalinks = [] as any
  for (const item of stylePermalinksQuery.data.findManyStyle) {
    // We are looping each item...

    let currentStylePages = Math.ceil(
      item.product_containers.length / itemsPerPage,
    )

    currentStylePages = currentStylePages > 3 ? 3 : currentStylePages

    for (let i = 1; i <= currentStylePages; i++) {
      const styleProfileIndex: number = getProfileIndexFromLanguage(
        item.style_profiles,
        language,
      )
      const permalink =
        item.style_profiles[styleProfileIndex]?.meta_information?.permalink

      const fullLink = `${permalink}/${i}`

      const sitemapObject = {
        loc: fullLink, // Absolute url
        lastmod: item.updatedAt,
        changefreq: 'daily',
        priority: 0.7,
      }

      stylePermalinks.push(sitemapObject)
    }
  }

  let paths = [] as any
  for (const item of stylePermalinks) {
    const paramObj = {
      params: {
        slug: item.loc.split('/'),
      },
      locale: language,
    }
    paths.push(paramObj)
  }

  // console.log('paths :>> ', paths)

  return {
    paths: paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
  defaultLocale,
}) => {
  const apolloClient = initializeApollo()
  const SHOP_ID = JSON.parse(process.env.NEXT_PUBLIC_SHOP_ID)
  const numberOfProductsToTake: number = itemsPerPage

  // === Handle a case, where link is opened without a number at the end...
  // e.g. http://localhost:3000/en/style/modern
  // ---------
  const permalink =
    params.slug.length > 1
      ? params.slug[params.slug.length - 2]
      : params.slug[0]
  const pageNumber =
    params.slug.length > 1 ? params.slug[params.slug.length - 1] : 1

  try {
    const staticData = await apolloClient.query({
      query: GET_SORTED_BY_POSITION_PRODUCTS_STYLE,
      variables: {
        permalink,
        language: locale,
        take: numberOfProductsToTake,
        page: parseInt(pageNumber as string),
      },
    })

    //* ==================================================================================
    //* ==================== Queries of data to pass to page component ===================
    //* ==================================================================================

    const currentStyleData = await apolloClient.query({
      query: GET_STYLE_DATA,
      variables: {
        permalink: permalink,
      },
    })

    const productsMinMaxPrice = await apolloClient.query({
      query: GET_MIN_MAX_PRICE_STATIC,
      variables: {
        permalink: permalink,
        language: locale,
      },
    })

    const productsMinMaxDimensions = await apolloClient.query({
      query: GET_MIN_MAX_DIMENSIONS_STATIC,
      variables: {
        permalink: permalink,
        language: locale,
      },
    })

    const shopSettingsData = await apolloClient.query({
      query: APP_SHOP_SETTINGS,
      variables: {
        SHOP_ID,
        isHomepage: false,
        isProductPage: false,
      },
    })

    // --- CATEGORY =============
    // const categoriesData = await apolloClient.query({
    //   query: GET_PARENT_CATEGORIES,
    //   variables: {
    //     language: locale,
    //   },
    // })
    const categoriesDataQuery = await apolloClient.query({
      query: GET_PARENT_CATEGORIES_NON_RECURSIVE,
      variables: {
        language: locale,
      },
    })
    let allCategories = [
      ...JSON.parse(JSON.stringify(categoriesDataQuery.data.findManyCategory)),
    ]
    const sortedByMenuOrder = allCategories.sort(
      (a, b) => a.menu_order - b.menu_order,
    )
    const sortedCategories = sortCategories(sortedByMenuOrder)
    // const flatCategories = flattenCategories(sortedCategories)
    let categoriesData = { ...JSON.parse(JSON.stringify(categoriesDataQuery)) }
    categoriesData.data.findManyCategory = sortedCategories
    // ---------------- END  CATEGORY =============

    return addApolloState(apolloClient, {
      props: {
        currentStyleData,
        staticData,
        categoriesData,
        styleName:
          currentStyleData?.data?.findUniqueMetaInformation?.style_profile
            ?.name ?? '',
        styleDescription:
          currentStyleData?.data?.findUniqueMetaInformation?.style_profile
            ?.description ?? '',
        permalink: permalink,
        productsMinMaxPrice,
        productsMinMaxDimensions,
        shopSettingsData,
        pageNumber: parseInt(pageNumber as string),
        locale,
        locales,
        defaultLocale,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 3600, // In seconds  i hr = 3600 12hrs = 43200
    })
  } catch (e) {
    // -----
    console.log('=================================================')
    console.log('Caught error in STYLE page GetStaticProps', e)
    return { notFound: true }
  }
}

// export const getServerSideProps: GetServerSideProps = async ({
//   params,
//   locale,
//   locales,
//   defaultLocale,
//   req,
// }) => {
//   const apolloClient = initializeApollo()
//   const SHOP_ID = JSON.parse(process.env.NEXT_PUBLIC_SHOP_ID)
//   const numberOfProductsToTake: number = 32

//   // === Handle a case, where link is opened without a number at the end...
//   // e.g. http://localhost:3000/en/style/modern
//   // ---------
//   const permalink =
//     params.slug.length > 1
//       ? params.slug[params.slug.length - 2]
//       : params.slug[0]
//   const pageNumber =
//     params.slug.length > 1 ? params.slug[params.slug.length - 1] : 1

//   const staticData = await apolloClient.query({
//     query: GET_SORTED_BY_POSITION_PRODUCTS_STYLE,
//     variables: {
//       permalink,
//       language: locale,
//       take: numberOfProductsToTake,
//       page: parseInt(pageNumber as string),
//     },
//   })

//   //* ==================================================================================
//   //* ==================== Queries of data to pass to page component ===================
//   //* ==================================================================================

//   const currentStyleData = await apolloClient.query({
//     query: GET_STYLE_DATA,
//     variables: {
//       permalink: permalink,
//     },
//   })

//   const productsMinMaxPrice = await apolloClient.query({
//     query: GET_MIN_MAX_PRICE_STATIC,
//     variables: {
//       permalink: permalink,
//       language: locale,
//     },
//   })

//   const productsMinMaxDimensions = await apolloClient.query({
//     query: GET_MIN_MAX_DIMENSIONS_STATIC,
//     variables: {
//       permalink: permalink,
//       language: locale,
//     },
//   })

//   const shopSettingsData = await apolloClient.query({
//     query: APP_SHOP_SETTINGS,
//     variables: {
//       SHOP_ID,
//       isHomepage: false,
//       isProductPage: false,
//     },
//   })

//   // --- Category menu component
//   const categoriesData = await apolloClient.query({
//     query: GET_PARENT_CATEGORIES,
//     variables: {
//       language: locale,
//     },
//   })

//   return addApolloState(apolloClient, {
//     props: {
//       uaString: req.headers['user-agent'],
//       currentStyleData,
//       staticData,
//       categoriesData,
//       styleName: currentStyleData.data.findUniqueMetaInformation.style_profile.name,
//       styleDescription:
//         currentStyleData.data.findUniqueMetaInformation.style_profile.description,
//       permalink: permalink,
//       productsMinMaxPrice,
//       productsMinMaxDimensions,
//       shopSettingsData,
//       pageNumber: parseInt(pageNumber as string),
//       locale,
//       locales,
//       defaultLocale,
//       ...(await serverSideTranslations(locale, ['common'])),
//     },
//   })
// }

export default Style
